import React from "react";
import { PageIntro } from "../components/PageIntro";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { MICROGREENS, IMAGES, waLink } from "../lib/data";
import { ArrowUpRight } from "lucide-react";

export default function Microgreens() {
  return (
    <div data-testid="microgreens-page">
      <PageIntro
        label="Our microgreens"
        titleLines={["A rotating range,", "selected for", "flavour & nutrition."]}
        subtitle="Our units grow a rotating range of microgreen varieties. Availability varies by season and by your local unit — your grower's live menu is always on WhatsApp."
      />

      {/* Varieties grid */}
      <section className="px-5 md:px-10 py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto">
          <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {MICROGREENS.map((m, i) => (
              <StaggerItem key={m.name}>
                <div className="group h-full bg-brand-card rounded-[1.5rem] p-8 border border-black/5 transition-transform duration-500 hover:-translate-y-2">
                  <div className="flex items-baseline justify-between">
                    <h3 className="font-serif text-3xl md:text-4xl text-brand-ink">{m.name}</h3>
                    <span className="font-mono text-xs text-brand-sprout">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <p className="mt-4 text-brand-muted leading-relaxed">{m.note}</p>
                </div>
              </StaggerItem>
            ))}
            <StaggerItem>
              <a
                href={waLink("Hi 40X Leaves, what's on my local grower's menu this week? My pincode is ")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="live-menu-cta"
                className="group h-full flex flex-col justify-between bg-brand-forest text-brand-bg rounded-[1.5rem] p-8 transition-transform duration-500 hover:-translate-y-2"
              >
                <p className="font-serif text-2xl md:text-3xl leading-snug">
                  See this week's live menu for your pincode.
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold">
                  Ask on WhatsApp <ArrowUpRight size={18} />
                </span>
              </a>
            </StaggerItem>
          </StaggerGroup>
        </div>
      </section>

      {/* How to use */}
      <section className="px-5 md:px-10 py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-6">
            <div className="overflow-hidden rounded-[2rem] rounded-tr-[6rem] h-72 md:h-[30rem]">
              <img src={IMAGES.hands} alt="Freshly harvested greens" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-6">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-brand-sprout mb-6">
              How to use
            </p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-brand-ink leading-tight">
              They work with everyday Kerala cooking, not against it.
            </h2>
            <p className="mt-6 text-brand-muted text-lg leading-relaxed">
              Toss into salads, top your dosa or sandwich, blend into smoothies, garnish curries and
              rice bowls — microgreens fit seamlessly into your kitchen.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Freshness promise */}
      <section className="px-5 md:px-10 pb-24 md:pb-32">
        <Reveal>
          <div className="max-w-[1400px] mx-auto rounded-[2rem] bg-brand-dark text-brand-bg p-8 py-16 md:p-20 relative overflow-hidden">
            <div className="noise-overlay" />
            <div className="relative max-w-3xl">
              <p className="font-mono text-xs uppercase tracking-[0.28em] text-brand-sprout mb-6">
                Freshness promise
              </p>
              <h2 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-tight">
                Every box is harvested on the day of delivery. If your greens ever arrive less than
                fresh, message your grower on WhatsApp and we'll make it right.
              </h2>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
